import { useCallback, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { dayjs } from 'utils/providers/Timezone';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { Filter, FilterForm, Searchbox } from 'ui/components/form';
import { sanitizeParams } from 'utils/helpers';
import { useGetAccountUsers } from 'modules/account/contact/hooks';
import { useQueryParams } from 'hooks';
import FILTER_TYPES from 'ui/components/form/FilterForm/filterTypes';
import { Button } from 'ui/components/common';
import { TaskCard } from 'modules/account/sales/components';
import { useGetTasks } from 'modules/account/sales/hooks';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';
import { Dropdown } from 'ui/components/nav';
import { debounce, size } from 'lodash';
import { DATE_OPTIONS } from 'utils/constants';

const FILTER_INIT = {
  accountUserId: [],
  status: [],
  type: [],
};

const FILTER_OPTIONS = {
  issuedAt: FILTER_TYPES.date,
  leadId: FILTER_TYPES.accountUsers,
  dueAt: FILTER_TYPES.date,
  type: FILTER_TYPES.taskType,
};

const Tasks = () => {
  const navigate = useNavigate();
  const {
    state: { relativePath },
  } = useAppWrapperContext();
  const [query, setQuery] = useQueryParams(FILTER_INIT);

  const {
    data: tasks,
    hasNextPage,
    fetchNextPage,
    isLoading,
  } = useGetTasks({ ...query, status: 'ARCHIVED' });
  const { ref, inView } = useInView();

  const [startDueAt, setStartDueAt] = useState<Date | undefined>(undefined);
  const [endDueAt, setEndDueAt] = useState<Date | undefined>(undefined);
  const [usersSearch, setUsersSearch] = useState('');
  const [filterOptions, setFilterOptions] = useState(FILTER_OPTIONS);

  const { data: accountUserData } = useGetAccountUsers({
    keyword: usersSearch,
  });

  const dueAt = useMemo(
    () =>
      startDueAt && endDueAt
        ? {
            dueAt: JSON.stringify({
              $gte: dayjs(startDueAt).toDate(),
              $lt: dayjs(endDueAt).add(1, 'days').toDate(),
            }),
          }
        : {},
    [startDueAt, endDueAt],
  );

  useEffect(() => {
    let accountUserMap: { itemLabel: string; value: string }[] = [];

    if (accountUserData?.data.length) {
      accountUserMap = accountUserData?.data?.map((user) => ({
        itemLabel: user.fullName,
        value: user._id,
      }));
    }

    setFilterOptions((prevVal) => ({
      ...prevVal,
      accountUserId: accountUserMap,
    }));
  }, [accountUserData]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  useEffect(() => {
    setQuery((prev) => ({
      ...prev,
      ...dueAt,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dueAt]);

  const handleFilterOnChange = (filters: { [key: string]: any }) => {
    setQuery({
      ...(size(dueAt) > 0 && { dueAt: dueAt as string }),
      ...sanitizeParams(filters),
    });
  };

  const onChangeDueAt = (dates: (Date | undefined)[]) => {
    const [start, end] = dates;
    setStartDueAt(start);
    setEndDueAt(end);
  };

  const handleBackToTasks = () => {
    navigate(`${relativePath}sales/tasks`);
  };

  const handleSearch = debounce((val: string) => setUsersSearch(val), 200);

  const accountUsersOptions = useCallback(
    (values: string[]) =>
      accountUserData?.data?.map((users) => ({
        value: users._id,
        label: users.fullName,
        description: users.emailAddress,
        isSelected: values.includes(users._id),
        withCheckbox: true,
      })) ?? [],
    [accountUserData?.data],
  );

  const taskTypeOptions = useCallback(
    (values: string[]) =>
      filterOptions.type.map((type) => ({
        value: type.value,
        label: type.label,
        isSelected: values.includes(type.value),
        withCheckbox: true,
      })) ?? [],
    [filterOptions.type],
  );

  return (
    <>
      <p className="mb-[22px] text-[16px] font-semibold">Tasks</p>
      <div className={cn('py-4 px-[22px]', 'rounded-[6px] bg-fill-tertiary')}>
        <FilterForm initialValues={FILTER_INIT} onChange={handleFilterOnChange}>
          {({ values, handleChange }) => (
            <div className="flex items-center justify-between">
              <div>
                <div className="flex gap-x-3">
                  <Searchbox
                    className="sm w-[220px]"
                    name="keyword"
                    placeholder="Search task title"
                    onChange={(e) => handleChange('keyword', e.target.value)}
                  />
                  <Filter
                    isMulti
                    placeholder="Assignee"
                    defaultValue={values.accountUserId}
                    options={accountUsersOptions(values.accountUserId)}
                    withSearch
                    onSearch={(e) => handleSearch(e)}
                    onClear={() => {
                      setUsersSearch('');
                      handleChange('accountUserId', []);
                    }}
                    onChange={(opt) => {
                      let accountUsers = [...values.accountUserId];

                      if (accountUsers.includes(opt.value)) {
                        accountUsers = [...values.accountUserId].filter(
                          (id) => id !== opt.value,
                        );
                      } else {
                        accountUsers.push(opt.value);
                      }

                      handleChange('accountUserId', accountUsers);
                    }}
                  />
                  <Dropdown
                    placeholder="Task Type"
                    options={taskTypeOptions(values.type)}
                    onChange={(opt) => handleChange('type', opt.value)}
                  />
                  <Filter
                    placeholder="Due At"
                    options={DATE_OPTIONS}
                    isDateField
                    startDate={startDueAt}
                    endDate={endDueAt}
                    onChangeDate={(dates) => {
                      onChangeDueAt(dates);
                    }}
                    onClear={() => {
                      handleChange('dueAt', undefined);
                      setStartDueAt(undefined);
                      setEndDueAt(undefined);
                    }}
                    onChange={(opt) => handleChange('dueAt', opt.value)}
                  />
                </div>
              </div>
              <Button
                intent={'tertiary'}
                label="BACK TO TASKS"
                className="w-[138px] font-semibold"
                onClick={handleBackToTasks}
              />
            </div>
          )}
        </FilterForm>
      </div>
      <div className="mt-[22px] h-[calc(100vh-330px)] overflow-y-auto">
        <div className="grid grid-cols-5 gap-4 2xl:grid-cols-6">
          {tasks?.pages
            ?.flatMap((task) => task.data)
            .map((task) => (
              <TaskCard
                key={task._id}
                task={task}
                isLoading={isLoading}
                isArchived
              />
            ))}
        </div>
        <p ref={ref} className="text-center text-sm">
          {hasNextPage ? 'Fetching...' : ' '}
        </p>
      </div>
    </>
  );
};

export default Tasks;
